import React from 'react';
import { Loader, Pagination } from '@mantine/core';
import { Button } from '../../../components/Button';
import {
  RESTIGO_KIND_LABELS,
  RESTIGO_STATUS_LABELS,
  resolveStatusClass,
} from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type RestigoCallLogsTableProps = Pick<
  UseMonitoringPage,
  | 'restigoRows'
  | 'isRestigoLoading'
  | 'hasLoadedRestigo'
  | 'restigoTotalPages'
  | 'restigoPage'
  | 'setRestigoPage'
  | 'openRestigoDetail'
  | 'resendRestigo'
  | 'isResendingRestigo'
>;

const statusBadgeClass = (status: string) => {
  if (status === 'success') return classes.levelBadgeMuted;
  if (status === 'timeout') return classes.levelBadgeWarn;
  return classes.levelBadgeError;
};

const httpBadgeClass = (statusClass: '4xx' | '5xx' | null) =>
  statusClass === '5xx' ? classes.statusBadge5xx : statusClass === '4xx' ? classes.statusBadge4xx : classes.levelBadgeMuted;

export const RestigoCallLogsTable: React.FC<RestigoCallLogsTableProps> = ({
  restigoRows,
  isRestigoLoading,
  hasLoadedRestigo,
  restigoTotalPages,
  restigoPage,
  setRestigoPage,
  openRestigoDetail,
  resendRestigo,
  isResendingRestigo,
}) => {
  if (isRestigoLoading && !hasLoadedRestigo) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table} data-testid="monitoring-restigo-table">
        <thead>
          <tr>
            <th>{'תאריך ושעה'}</th>
            <th>{'עסק'}</th>
            <th>{'דוח'}</th>
            <th>{'סטטוס'}</th>
            <th>{'קוד HTTP'}</th>
            <th>{'זמן תגובה'}</th>
            <th>{'הפעלה'}</th>
            <th>{'שגיאה'}</th>
            <th>{'פעולה'}</th>
          </tr>
        </thead>
        <tbody>
          {restigoRows.map((row) => (
            <tr
              key={row.id}
              className={classes.clickableRow}
              role="button"
              tabIndex={0}
              data-testid={`monitoring-restigo-row-${row.id}`}
              onClick={() => openRestigoDetail(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openRestigoDetail(row);
                }
              }}
            >
              <td data-label={'תאריך ושעה'}>{formatDateTime(row.createdAt)}</td>
              <td data-label={'עסק'}>{row.companyName ?? row.tenantId ?? '-'}</td>
              <td data-label={'דוח'}>{RESTIGO_KIND_LABELS[row.kind] ?? row.kind}</td>
              <td data-label={'סטטוס'}>
                <span className={`${classes.detailKindBadge} ${statusBadgeClass(row.status)}`}>
                  {RESTIGO_STATUS_LABELS[row.status] ?? row.status}
                </span>
              </td>
              <td data-label={'קוד HTTP'}>
                {row.httpStatus != null ? (
                  <span className={`${classes.detailKindBadge} ${httpBadgeClass(resolveStatusClass(row.httpStatus))}`}>
                    {row.httpStatus}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td data-label={'זמן תגובה'}>{row.latencyMs != null ? `${row.latencyMs} ms` : '-'}</td>
              <td data-label={'הפעלה'}>{row.zNumber ?? (row.sessionId != null ? String(row.sessionId) : '-')}</td>
              <td data-label={'שגיאה'}>
                <div className={classes.errorMessageCell}>{row.errorMessage ?? '-'}</div>
              </td>
              <td data-label={'פעולה'} onClick={(event) => event.stopPropagation()}>
                {row.status !== 'success' && row.sessionId != null && (
                  <Button
                    variant="save"
                    size="sm"
                    onClick={() => resendRestigo(row)}
                    disabled={isResendingRestigo}
                    dataTestId={`monitoring-restigo-resend-${row.id}`}
                  >
                    {'שלח שוב'}
                  </Button>
                )}
              </td>
            </tr>
          ))}
          {restigoRows.length === 0 && (
            <tr>
              <td colSpan={9} className={classes.empty}>
                {'אין רשומות מעקב רסטיגו לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {restigoTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={restigoTotalPages} value={restigoPage} onChange={setRestigoPage} size="sm" />
        </div>
      )}
    </div>
  );
};
